import { useMemo } from 'react'
import { selectPreviewedPlanSettings } from '@scene'
import { useGetPlanSettings, usePlanLabelPdfMetrics } from '@editor/hooks'
import useIsStiffeningPlanPreview from '@editor/pages/PlanView/hooks/useIsStiffeningPlanPreview'
import useStiffeningPlanAnchorLabels from '@editor/pages/PlanView/hooks/useStiffeningPlanAnchorLabels'
import { useControlStore, useStiffeningStore } from '@editor/stores'
import { getStiffeningSegmentPlanAxis } from '@editor/utils'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import { LabelPlacement } from 'src/components/scene/ModelElementLabels/utils'
import { BEAM_LABEL_BORDER_WIDTH, STIFFENING_PLAN_ANCHOR_LABEL_COLOR } from './constants'
import {
  getShearAnchorLabelPlacement,
  getStoreyTopZLookup,
  getTensileAnchorLabelPlacement,
} from './labelPlacements'
import { getPdfLabelBoxDescriptorProps } from './pdfLabelBox'
import {
  getStiffeningAnchorLabelGuid,
  StiffeningAnchorLabelKind,
} from './stiffeningAnchorLabelGuid'

interface Params {
  visibleModel: PlanarModel
  guidToHidden: Record<string, boolean>
  showInnerWallLabels: boolean
  showOuterWallLabels: boolean
}

/**
 * The stiffening plan preview's Zuganker (at each segment end) and Schubanker (beside the
 * segment) labels, drawn like the export's `anchor_label`: a blue bordered box on white.
 *
 * They follow the wall label toggles, like the segment labels. Their descriptor guids are derived
 * from the segment guid (`getStiffeningAnchorLabelGuid`), which also keys their user-moved plan
 * label positions.
 *
 * The Schubanker label carries its count ("3x SA1") when "Anzahl Schubanker ausgeben" is set in
 * the plan settings, following a plan settings form's unsaved values while it previews them
 * (`selectPreviewedPlanSettings`), like the plan area frame does.
 */
const useStiffeningAnchorLabelDescriptors = ({
  visibleModel,
  guidToHidden,
  showInnerWallLabels,
  showOuterWallLabels,
}: Params): ElementLabelDescriptor[] => {
  const isStiffeningPlan = useIsStiffeningPlanPreview()
  const anchorLabelsPerSegment = useStiffeningPlanAnchorLabels(isStiffeningPlan)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const metrics = usePlanLabelPdfMetrics()
  const { data: savedPlanSettings } = useGetPlanSettings()
  const previewedPlanSettings = useControlStore(selectPreviewedPlanSettings)
  const showShearAnchorCount = !!(previewedPlanSettings ?? savedPlanSettings)?.showShearAnchorCount

  return useMemo(() => {
    if (!isStiffeningPlan) return []

    const storeyTopZ = getStoreyTopZLookup(visibleModel)
    const wallsByGuid = new Map(visibleModel.walls.map(wall => [wall.guid, wall]))

    return stiffeningIntervals.reduce((collector: ElementLabelDescriptor[], segment) => {
      // Unlike the segment labels, not limited to stiffening segments: the export labels the
      // anchors of every segment of a wall, so whichever the backend returns labels for.
      if (!segment.guid) return collector
      const anchorLabels = anchorLabelsPerSegment[segment.guid as string]
      if (!anchorLabels) return collector

      const wall = wallsByGuid.get(segment.element_guid)
      if (!wall || guidToHidden[wall.guid]) return collector
      const isExternal = wall.placement === 'External'
      if (isExternal ? !showOuterWallLabels : !showInnerWallLabels) return collector

      const axis = getStiffeningSegmentPlanAxis(wall, segment)
      if (!axis) return collector
      const topZ = storeyTopZ[wall.storey]

      const labels: {
        kind: StiffeningAnchorLabelKind
        text: string | null | undefined
        placement: (text: string) => LabelPlacement
      }[] = [
        {
          kind: 'tensile-anchor-start',
          text: anchorLabels.tensile_anchor_start,
          placement: () => getTensileAnchorLabelPlacement(axis, 'start', topZ),
        },
        {
          kind: 'shear-anchor',
          text: showShearAnchorCount
            ? anchorLabels.shear_anchor_with_count
            : anchorLabels.shear_anchor,
          placement: (text: string) => getShearAnchorLabelPlacement(axis, text, metrics, topZ),
        },
        {
          kind: 'tensile-anchor-end',
          text: anchorLabels.tensile_anchor_end,
          placement: () => getTensileAnchorLabelPlacement(axis, 'end', topZ),
        },
      ]
      labels.forEach(({ kind, text, placement }) => {
        if (!text) return
        const textLines = [text]
        collector.push({
          guid: getStiffeningAnchorLabelGuid(segment.guid as string, kind),
          textLines,
          ...placement(text),
          ...getPdfLabelBoxDescriptorProps(textLines, metrics),
          color: STIFFENING_PLAN_ANCHOR_LABEL_COLOR,
          opacity: 1,
          // flat text, like the other floorplan labels
          textDepth: 0,
          backgroundColor: '#FFFFFF',
          borderColor: STIFFENING_PLAN_ANCHOR_LABEL_COLOR,
          borderWidth: BEAM_LABEL_BORDER_WIDTH,
        })
      })
      return collector
    }, [])
  }, [
    isStiffeningPlan,
    anchorLabelsPerSegment,
    stiffeningIntervals,
    visibleModel,
    guidToHidden,
    showInnerWallLabels,
    showOuterWallLabels,
    metrics,
    showShearAnchorCount,
  ])
}

export default useStiffeningAnchorLabelDescriptors
